import { Component, OnInit } from '@angular/core';
import { Auth, getIdTokenResult } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { AumicoCoreProvider, NotificationType } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import Debug from 'debug';
import { filter } from 'rxjs';
import { password } from '../../actions/auth.actions';
import { localizationKeys } from '../../constants/localizationKeys';
import { IChangePasswordRequest } from '../../interfaces/change-password-request.interface';
import { AuthProvider } from '../../provider/auth.provider';
const debug = Debug('aumico:aumico-lib-users-fe:UserSecurityPage');

const DEFAULT_LANGUAGE = 'de-ch';
// URLs are `/{lang}/…`: index 0 is the empty segment before the first slash.
const LANGUAGE_URL_SEGMENT_INDEX = 1;

@UntilDestroy()
@Component({
    selector: 'aumico-user-security-page',
    templateUrl: './user-security.page.html',
    styleUrls: ['./user-security.page.scss'],
})
export class UserSecurityPage implements OnInit {
    readonly localizationKeys = localizationKeys;
    /** Why the user has to log in again after a password change; `null` while no relogin is needed. */
    reloginReason: 'changed' | 'failed' | null = null;
    /** `auth_time` of the login before the password change — the change signs in again and gets a new one. */
    private authTimeBeforeChange: string | null = null;

    constructor(
        private readonly authProvider: AuthProvider,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly auth: Auth,
        private readonly router: Router,
    ) {}
    ngOnInit(): void {
        this.actionsListener$.pipe(ofType(password.changeSuccess), untilDestroyed(this)).subscribe(() => {
            void this.onPasswordChanged();
        });
        this.authProvider
            .getUsersError$()
            .pipe(
                filter((error) => error !== undefined),
                untilDestroyed(this),
            )
            .subscribe((error) => {
                debug(error);
                void this.onPasswordChangeFailed();
            });
    }

    /**
     * REV-22124: a password change signs in again and so starts a new login (new `auth_time`) that has not passed
     * 2FA. In an org with 2FA every further API call would be refused with 403 and the interceptor would log out
     * without explanation, so the user is asked to log in again (password + code) before anything else happens.
     * Reads only the token already in the SDK — no API call.
     */
    private async onPasswordChanged(): Promise<void> {
        const login = await this.readCurrentLogin();
        if (login?.mfaRequired === true) {
            this.reloginReason = 'changed';
            return;
        }

        this.fireSuccessNotification();
    }

    /** The new sign-in may have succeeded before `updatePassword` failed (e.g. weak password) — same new login. */
    private async onPasswordChangeFailed(): Promise<void> {
        this.fireFailedNotification();

        const login = await this.readCurrentLogin();
        const isNewLogin = login != null && this.authTimeBeforeChange != null && login.authTime !== this.authTimeBeforeChange;
        if (login?.mfaRequired === true && isNewLogin) {
            this.reloginReason = 'failed';
        }
    }

    private async readCurrentLogin(): Promise<{ mfaRequired: boolean; authTime: string } | null> {
        try {
            const currentUser = this.auth.currentUser;
            if (currentUser == null) {
                return null;
            }
            const idTokenResult = await getIdTokenResult(currentUser);
            return { mfaRequired: idTokenResult.claims['mfaRequired'] === true, authTime: idTokenResult.authTime };
        } catch (error) {
            debug(error);
            return null;
        }
    }

    logout(): void {
        const language = this.router.url.split('/')[LANGUAGE_URL_SEGMENT_INDEX] || DEFAULT_LANGUAGE;
        void this.router.navigate([`${language}/logout`]);
    }

    private fireSuccessNotification(): void {
        const SUCCESS_DESCRIPTION_LOCALIZATION_KEY = localizationKeys.aumico_change_password_notification_success;
        const SUCCESS_TITLE_LOCALIZATION_KEY = localizationKeys.aumico_change_password_notification_success_title;
        this.coreProvider.dispatchAddNotification(
            SUCCESS_TITLE_LOCALIZATION_KEY,
            SUCCESS_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.SUCCESS,
        );
    }

    private fireFailedNotification(): void {
        const FAILED_DESCRIPTION_LOCALIZATION_KEY = localizationKeys.aumico_change_password_notification_failed;
        const FAILED_TITLE_LOCALIZATION_KEY = localizationKeys.aumico_change_password_notification_failed_title;
        this.coreProvider.dispatchAddNotification(
            FAILED_TITLE_LOCALIZATION_KEY,
            FAILED_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.ERROR,
        );
    }

    async onPasswordChange(changedPassword: IChangePasswordRequest): Promise<void> {
        this.authTimeBeforeChange = (await this.readCurrentLogin())?.authTime ?? null;
        this.authProvider.dispatchChangePassword(changedPassword);
    }
}
